import React, {useCallback, useEffect, useState} from 'react';
import {BasicTree} from '../BasicTree/BasicTree';
import {Button, FormGroup, Label} from 'reactstrap';
import {useForm} from 'react-hook-form';
import InformationHelper from '../InformationHelper';
import {equals, filter, includes, is, isNil, keys} from 'ramda';
import {Typeahead} from 'react-bootstrap-typeahead';
import DatePickerInput from './Datepicker';
import {FBCheckboxGroup, FBFileUpload, FBSelect} from './CustomInputs';
import type {FormBuilderProps} from './form.types';
import Avatar from '../Avatar';
import {lookupInputType} from "../AppMenu/redux/utils";

const FormBuilder = (props: FormBuilderProps) => {
    const {children, form, className, onSubmit, close} = props;
    const isCustomInputs = x => includes(x.type, ['dropdown', 'datetime', 'tree', 'typeahead']);
    const customInputs = filter(isCustomInputs, form.inputs);

    const initialState = {};
    customInputs.forEach(i => initialState[i.name] = i.value);
    const [customInputState, setCustomInputState] = useState(initialState);

    // react-hook-form
    const {register, handleSubmit, setValue, setError, errors, getValues, reset} = useForm();

    // Register custom components with react-hook-form
    useEffect(_ => {
        let hasInputChange = false;
        const customState = {...customInputState};

        customInputs.forEach(input => {
            const validations = isNil(input.validations) ? {} : input.validations;
            register({name: input.name}, validations);
            if (isNil(customState[input.name]) && !isNil(input.value)) {
                customState[input.name] = input.value;
                hasInputChange = true;
            }
        });

        if (hasInputChange) {
            setCustomInputState(customState);
        }

    }, [form]);

    const onFormSubmit = useCallback(data => {
        // Flatten data for dropdown list
        keys(customInputState).forEach(k => data[k] = customInputState[k]);
        keys(data).forEach(k => {
            const value = data[k];
            const isSingleOption = is(Object, value) && equals(keys(value), ['label', 'value']);
            const isMultiOptions = is(Array, value) && value.length > 0 && keys(value[0]).length === 2 && equals(keys(value, ['value', 'label']));
            if (isSingleOption) {
                data[k] = value.value;
            } else if (isMultiOptions) {
                data[k] = value.map(o => o.value);
            }
        });

        onSubmit(data);
        handleClose();
    });

    const handleCustomInputChange = useCallback((name, value) => {
        const type = lookupInputType(name, form.inputs);

        if (type === 'dropdown') {
            value = value ? value : [];
        }

        setValue(name, value);
        const newState = {...customInputState};
        newState[name] = value;
        setCustomInputState({...newState});

        if (props.onChange) {
            props.onChange(getValues());
        }
    });

    const handleInputChange = useCallback(_ => {
        if (props.onChange) {
            props.onChange(getValues());
        }
    });

    const handleCustomInputError = useCallback((name, type, error) => {
        setError(name, type, error);
    });

    const inputs = props.form.inputs.map((input, index) => {
        return (
            <FormGroup key={index}>
                {!input.hidden && !equals(input.type, 'checkbox') && (
                    <Label>
                        {input.label}
                        {input.help && (
                            <InformationHelper name={input.name} text={input.help}/>
                        )}
                    </Label>
                )}

                {equals(input.type, 'avatar') && !input.hidden && (
                    <div className="mb-3 w-100">
                        <Avatar value={input.value}
                                name={input.name}
                                onChange={handleCustomInputChange}
                                username={input.defaultValue || 'User'}
                        />
                    </div>
                )}

                {(equals(input.type, 'tree') && !input.hidden) && (
                    <BasicTree
                        required={input.required}
                        onCheck={handleCustomInputChange}
                        name={input.name}
                        checkedKeys={customInputState[input.name]}
                        nodes={input.treeData.nodes}
                    />
                )}

                {(equals(input.type, 'input') || equals(input.type, 'textarea')) && !input.hidden && !input.controlled && (
                    <input name={input.name}
                           placeholder={input.placeholder}
                           autoComplete={input.autoComplete ? 'on' : 'off'}
                           autoFocus={input.autofocus || false}
                           type={input.formType || input.type}
                           className="form-control"
                           defaultValue={input.value || ''}
                           disabled={input.disabled || false}
                           hidden={input.hidden || false}
                           ref={register(input.validations || {})}
                           onChange={handleInputChange}
                    />
                )}

                {(equals(input.type, 'input') || equals(input.type, 'textarea')) && !input.hidden && input.controlled && (
                    <input name={input.name}
                           placeholder={input.placeholder}
                           autoComplete={input.autoComplete ? 'on' : 'off'}
                           autoFocus={input.autofocus || false}
                           type={input.formType || input.type}
                           className="form-control"
                           value={input.value || ''}
                           disabled={input.disabled || false}
                           hidden={input.hidden || false}
                           ref={register(input.validations || {})}
                           onChange={handleInputChange}
                    />
                )}

                {(equals(input.type, 'dropdown') && !input.hidden && input.options) && !input.hidden && (
                    <FBSelect options={input.options}
                              isMulti={input.multiple}
                              name={input.name}
                              onChange={handleCustomInputChange}
                              onHoverTab={input.onHoverTab}
                              value={customInputState[input.name]}
                              className='dropdown-list' classNamePrefix='dropdown-list'
                              validations={input.validations || {}}
                    />
                )}

                {(equals(input.type, 'datetime') && !input.hidden) && (
                    <DatePickerInput
                        value={input.value}
                        data={handleCustomInputChange}
                        name={input.name}
                    />
                )}

                {equals(input.type, 'checkbox') && !input.hidden && (
                    <div className="custom-checkbox custom-control">
                        <input type="checkbox" id={input.name}
                               name={input.name}
                               defaultChecked={input.value || false}
                               className="custom-control-input"
                               hidden={input.hidden}
                               ref={register(input.validations || {})}
                               onChange={handleInputChange}
                        />
                        <label className="custom-control-label" htmlFor={input.name}>
                            {input.label}
                        </label>
                    </div>
                )}

                {equals(input.type, 'multi-checkbox') && !input.hidden && (
                    <FBCheckboxGroup options={input.options}
                                     name={input.name}
                                     disabled={input.disabled || false}
                                     hidden={input.hidden}
                                     onChange={handleCustomInputChange}
                                     onError={handleCustomInputError}
                                     validations={input.validations || {}}
                    />
                )}

                {(equals(input.type, 'typeahead') && input.options && !input.hidden) && (
                    <Typeahead
                        id='#typeahead'
                        className='w-100'
                        name={input.name}
                        labelKey={option => `${option[input.optionKey]}`}
                        onChange={value => handleCustomInputChange(input.name, value)}
                        options={input.options}
                    />
                )}

                {(equals(input.type, 'file') && !input.hidden) && (
                    <div className="mb-3 w-100 h-100">
                        <FBFileUpload
                            onFileUpload={handleCustomInputChange}
                            preview={input.preview}
                            previewMany={input.previewMany}
                        />
                    </div>
                )}

                {errors && errors[input.name] && (
                    <div className='mt-1 text-danger'>{errors[input.name].message}</div>
                )}
            </FormGroup>
        )
    });

    const handleClose = useCallback(_ => {
        setCustomInputState({});
        reset();
        if (close) {
            close();
        }
    });

    return (
        <>
            <form onSubmit={handleSubmit(onFormSubmit)} className='d-flex flex-fill flex-column'>
                <div className='form-content flex-fill'>
                    {inputs}
                    {children}
                </div>
                {!props.hideFooter && (
                    <FormGroup className={`modal-footer justify-content-start pb-0 ${className}`}>
                        <Button color="dark" onClick={handleClose}>Cancel</Button>
                        <Button color="primary" className='ml-2'>{form.submitButton || 'Submit'}</Button>
                    </FormGroup>
                )}
            </form>
        </>
    );
};

export default FormBuilder;
